import React, { useState } from 'react'
import {
  CancelOutlined,
  GetApp as GetAppIcon,
  Visibility,
  HighlightOff as HighlightOffIcon,
} from '@mui/icons-material'
import {
  Button,
  Typography,
  Dialog,
  DialogTitle,
  DialogActions,
  IconButton,
  CircularProgress,
  Box,
  DialogContent,
  Pagination,
  LinearProgress,
} from '@mui/material'
import axios from 'axios'
import { useAuthContext } from '../../hooks/useAuthContext'
import { fileServices } from '../../services/fileServices'
import { downloadBlob } from '../../utility/fileUtil'

const DocumentDialogBox = ({
  isDocOpen,
  title,
  setIsDocOpen,
  setTimelineOpen,
  docData,
  timelines,
  DocStyle,
}) => {
  const { user } = useAuthContext()
  const [isLoading, setIsLoading] = useState(false)
  const [loading, setLoading] = useState(new Set())
  const [previewLoading, setPreviewLoading] = useState(false)
  const [images, setImages] = useState([])
  const [currentPage, setCurrentPage] = useState(1)
  const NODE_BASE_URL = process.env.REACT_APP_NODE_BASE_URL
  const handlePageChange = (event, value) => {
    setCurrentPage(value)
  }
  const itemsPerPage = 1

  const totalPages = Math.ceil(images.length / itemsPerPage)

  const uniqueActivityIds = [...new Set(timelines.map((item) => item.activity_template_id))]
  const filteredTimeLine = docData
    ?.filter((item) => uniqueActivityIds.includes(item.activity_template_id))
    .sort((a, b) => a.sequence - b.sequence)

  const documentProcessId = [...new Set(docData.map((item) => item.process_id))]
  const filteredDocTimeLine = timelines?.filter((item) => documentProcessId.includes(item.id))

  const docHeaderStyle = {
    display: 'flex',
    justifyContent: 'space-between',
  }
  const docTitleStyle = {
    padding: '0 0 15px 26px',
    color: '#0000EE',
  }
  const docIconStyle = {
    padding: '0 26px 15px 0px',
    color: '#0000EE',
  }

  const downloadFile = async (fileName, index) => {
   
    if (loading.has(index)) {
      return
    }
 
   
    setLoading((prev) => new Set([...prev, index]))
    setIsLoading(true)
 
    try {
      const result = await fileServices.downloadFile(user, fileName, 'documents_for_template')
      if (result?.data) {
       
        downloadBlob(result?.data, fileName)
      }
    } catch (error) {
      console.error('Error downloading file:', error)
    } finally {
     
      setLoading((prev) => {
        const updated = new Set(prev)
        updated.delete(index)
        return updated
      })
      setIsLoading(false)
    }
  }

  const getPreviewData = async (key) => {
    try {
      setPreviewLoading(true)
      setIsDocOpen(false)

      const result = (
        await axios.get(
          `${NODE_BASE_URL}/data-entry/file/base64-images/${key}?folder=documents_for_template`,
          {
            headers: {
              Authorization: `Bearer ${user.token}`,
            },
          }
        )
      ).data

      setImages(result)
      setCurrentPage(result.length)
    } catch (e) {
      setImages([])
    }
  }

  return (
    <>
      <Dialog
        open={isDocOpen}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
        style={{ width: '-webkit-fill-available%' }}
        onClose={() => {
          setIsDocOpen(false)
          setTimelineOpen(true)
        }}
        fullWidth={true}
      >
        <div style={docHeaderStyle}>
          <DialogTitle id="alert-dialog-title">
            <div>{title}</div>
            {filteredDocTimeLine?.map((data, i) => (
              <Typography variant="subtitle2" component="span" key={i}>
                {`${data.activity_template_name} (version: ${data.version})`}
              </Typography>
            ))}
          </DialogTitle>
          <div style={{ display: 'inline-block', margin: '20px 28px 0px 0px' }}>
            <IconButton
              align="right"
              onClick={() => {
                setIsDocOpen(false)
                setTimelineOpen(true)
              }}
              size="large"
            >
              <HighlightOffIcon />
            </IconButton>
          </div>
        </div>

        {filteredTimeLine &&
          filteredTimeLine.map((file, i) => (
            <div
              key={i}
              style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
            >
              <span style={docTitleStyle}>{file.document_template_name}</span>

              <span style={docIconStyle}>
                <IconButton
                  size="small"
                  onClick={() => {
                    getPreviewData(file.document_key)
                    setIsDocOpen(true)
                  }}
                >
                  {images == null ? (
                    <CircularProgress color="primary" size={'25px'} />
                  ) : (
                    <Visibility size="small" />
                  )}
                </IconButton>

                <IconButton
                  size="small"
                  download
                  onClick={() => downloadFile(file.document_key, i)}
                >
                  {loading.has(i) ? (
                    <CircularProgress color="info" size={'25px'} />
                  ) : (
                    <GetAppIcon color="info" size="small" />
                  )}
                </IconButton>
              </span>
            </div>
          ))}
      </Dialog>
      <div>
        <Dialog
          open={previewLoading}
          aria-labelledby="alert-dialog-title"
          aria-describedby="alert-dialog-description"
          onClose={() => {
            setIsDocOpen(true)
            setPreviewLoading(false)
          }}
          maxWidth="xxl"
        >
          <Box sx={{ width: '100%' }}>
            {previewLoading && (
              <Box sx={{ width: '100%', position: 'sticky', top: 0 }}>
                <DialogContent>
                  {images.length === 0 ? (
                    <div>
                      Preview not available. Click the "Download" icon to download file{' '}
                      {/* <LinearProgress color="primary" /> */}
                    </div>
                  ) : (
                    <>
                      <div style={{ position: 'relative' }}>
                        {images && (
                          <img
                            style={{ width: '100%', height: 'auto' }}
                            src={images[currentPage - 1]}
                            alt="Image"
                          />
                        )}
                        <div style={{ position: 'absolute', top: 0, right: 0 }}>
                          <Button
                            color="info"
                            size="small"
                            onClick={() => {
                              setIsDocOpen(true)
                              setPreviewLoading(false)
                            }}
                          >
                            <CancelOutlined size="small" />
                          </Button>
                        </div>
                      </div>
                      {images && (
                        <Box
                          sx={{
                            maxWidth: '800px',
                            display: 'flex',
                            justifyContent: 'center',
                            padding: '10px',
                            backgroundColor: 'white',
                            position: 'fixed',
                            bottom: '5%',
                            left: '50%',
                            transform: 'translateX(-50%)',
                          }}
                        >
                          <Pagination
                            count={totalPages}
                            page={currentPage}
                            onChange={handlePageChange}
                            variant="outlined"
                            shape="rounded"
                          />
                        </Box>
                      )}
                    </>
                  )}
                </DialogContent>
              </Box>
            )}
          </Box>
        </Dialog>
      </div>
    </>
  )
}

export default DocumentDialogBox
